// istanbul ignore file

// externals
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import mitt from 'mitt';

// libraries
import { InterstateThemeProvider } from '@interstate/components/InterstateThemeProvider';
import { interstateThemeRegistry } from '@interstate/components/InterstateTheming/themes/interstateThemeRegistry';

import { setupAuthNetworkIntercepts } from '@makemydeal/dr-shared-network';
import { SalesCheckInSpinner } from '@makemydeal/dr-sales-checkin-components';
import { getDealerMMDId } from '@makemydeal/dr-sales-checkin-store';
import { configureStore, historyInstance, subscribeDealerIdToSession } from '@makemydeal/dr-shared-store';
import { AuthGateReadyContent, AuthManager, startAuthGateTelemetry } from '@makemydeal/dr-shared-components';
import { SALES_CHECKIN_APP_PREFIX, registerNewRelicNoiseFilter } from '@makemydeal/dr-shared-ui-utils';

// components
import { RoutingWrapper } from './shells/main/routing/routingWrapper';
import { Bootstrapper } from './shells/main/components/bootstrapper/Bootstrapper';

// utils
import { getInitialMiddleware } from './shells/main/store/initialMiddleware';
import { getRemainingMiddleware } from './shells/main/store/remainingMiddleware';

import { initialReducers } from './shells/main/reducers/initialReducers';
import { getAuthConfig } from './auth';
import QueryParamsHandler from './shells/main/routing/queryParamsHandler';
import { createTokenRenewalFailureHandler } from './shells/main/auth/createTokenRenewalFailureHandler';

// we don't have an initial state - it's provided by the init response
const state = {};
const allMiddleware = getInitialMiddleware().concat(getRemainingMiddleware());
const store = configureStore(state, allMiddleware, initialReducers);
subscribeDealerIdToSession(store, getDealerMMDId);

// This call will result in fetch and axios network calls being intercept to inject auth headers:
setupAuthNetworkIntercepts(SALES_CHECKIN_APP_PREFIX);
registerNewRelicNoiseFilter();

(window as Record<string, any>).caiEventBus = (window as Record<string, any>).caiEventBus || mitt();

const container = document.getElementById('root');

startAuthGateTelemetry({ pageActionName: 'SC:AuthGate' });

createRoot(container!).render(
    <>
        <InterstateThemeProvider
            themeName="Interstate"
            applicationName="SalesCheckin"
            scopeName="SalesCheckin"
            themeRegistries={[interstateThemeRegistry]}
        >
            <ReduxProvider store={store}>
                <ConnectedRouter history={historyInstance}>
                    {/* This component is responsible for storing search params in localStorage
                        so that it can be accesses later in the application after login */}
                    <QueryParamsHandler>
                        <AuthManager
                            appPrefix={SALES_CHECKIN_APP_PREFIX}
                            offerReadyContent={(props) => <AuthGateReadyContent {...props} loadingPage={SalesCheckInSpinner} />}
                            getConfig={getAuthConfig}
                            onSignOut={() => {
                                // no-op: logout already handled by AuthManager
                            }}
                            onTokenRenewalFailure={createTokenRenewalFailureHandler(store)}
                        >
                            <Bootstrapper>
                                <RoutingWrapper />
                            </Bootstrapper>
                        </AuthManager>
                    </QueryParamsHandler>
                </ConnectedRouter>
            </ReduxProvider>
        </InterstateThemeProvider>
    </>
);
